/**
 * TanStack Query hooks for Class resource (cohort classes).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

export type ClassStatus = 'planned' | 'open' | 'running' | 'finished' | 'cancelled';

export interface ClassListItem {
  id: string;
  code: string;
  name: string;
  branchId: string;
  branchName?: string;
  courseId: string;
  courseName?: string;
  courseLevel?: string;
  roomId?: string | null;
  roomName?: string | null;
  status: ClassStatus;
  startDate: string | null;
  endDate?: string | null;
  capacity: number;
  currentEnrollment: number;
  note?: string | null;
  leadTeacherName?: string | null;
  leadTeacherCode?: string | null;
}

export interface ClassDetail extends ClassListItem {
  createdAt: string;
  updatedAt: string;
}

export interface ListClassesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  branchId?: string;
  courseId?: string;
  status?: ClassStatus;
}

// --- Schedules ---
export type ScheduleMode = 'weekly' | 'single';
export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface ClassSchedule {
  id: string;
  classId: string;
  branchId: string;
  mode: ScheduleMode;
  weekday?: Weekday | null;
  sessionDate?: string | null;
  startTime: string;
  endTime: string;
  roomId?: string | null;
  roomName?: string | null;
}

// --- Teachers ---
export interface ClassTeacher {
  id: string;
  classId: string;
  teacherId: string;
  teacherName?: string;
  staffCode?: string;
  email?: string | null;
  role: 'lead' | 'assistant';
  assignedAt?: string;
  branchId?: string;
}

// --- Enrollments (for class roster tab) ---
export interface ClassEnrollment {
  id: string;
  studentId: string;
  studentCode?: string;
  studentName?: string;
  classId: string;
  status: string;
  enrolledAt?: string;
}

export const classKeys = {
  all: ['classes'] as const,
  list: (params?: ListClassesParams) => ['classes', 'list', params] as const,
  detail: (id: string) => ['classes', 'detail', id] as const,
  schedules: (id: string) => ['classes', id, 'schedules'] as const,
  teachers: (id: string) => ['classes', id, 'teachers'] as const,
  enrollments: (id: string) => ['classes', id, 'enrollments'] as const,
};

export function useClasses(params?: ListClassesParams) {
  return useQuery({
    queryKey: classKeys.list(params),
    queryFn: () =>
      api.getPage<ClassListItem>('/api/classes', params as Record<string, string>),
  });
}

export function useClass(id: string) {
  return useQuery({
    queryKey: classKeys.detail(id),
    queryFn: () => api.get<ClassDetail>(`/api/classes/${id}`),
    enabled: !!id,
  });
}

export function useCreateClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<ClassDetail>('/api/classes', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: classKeys.all });
    },
  });
}

export function useUpdateClass(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<ClassDetail>(`/api/classes/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: classKeys.all });
    },
  });
}

export function useClassSchedules(classId: string) {
  return useQuery({
    queryKey: classKeys.schedules(classId),
    queryFn: () =>
      api.get<ClassSchedule[]>(`/api/classes/${classId}/schedules`),
    enabled: !!classId,
  });
}

export function useClassTeachers(classId: string) {
  return useQuery({
    queryKey: classKeys.teachers(classId),
    queryFn: () =>
      api.get<ClassTeacher[]>(`/api/classes/${classId}/teachers`),
    enabled: !!classId,
  });
}

export function useClassEnrollments(classId: string) {
  return useQuery({
    queryKey: classKeys.enrollments(classId),
    queryFn: () =>
      api.getPage<ClassEnrollment>(`/api/enrollments`, { classId, pageSize: 100 }),
    enabled: !!classId,
  });
}
